import {
  type ComponentPropsWithoutRef,
  createElement,
  type ElementType,
  type ReactNode,
} from "react";

import styles from "./ScreenReaderOnly.module.scss";

type ScreenReaderOnlyProps<T extends ElementType = "div"> = {
  children: ReactNode;
  as?: T;
} & ComponentPropsWithoutRef<T>;

/**
 * ScreenReaderOnly component renders content that is visually hidden but accessible to screen readers.
 * Uses the "visually hidden" CSS technique to hide content from sighted users while keeping it
 * available to assistive technologies.
 *
 * @example
 * ```tsx
 * <ScreenReaderOnly>
 *   Additional context for screen reader users
 * </ScreenReaderOnly>
 *
 * <ScreenReaderOnly as="span" aria-live="polite">
 *   Status update
 * </ScreenReaderOnly>
 * ```
 */
function ScreenReaderOnly<T extends ElementType = "div">({
  children,
  as,
  ...props
}: ScreenReaderOnlyProps<T>) {
  // `createElement`, not JSX on a `T`-typed tag: the JSX props check intersects every intrinsic
  // element's props, which fails in any app whose JSX carries React Three Fiber's elements too.
  // The call site is still typed through `ScreenReaderOnlyProps<T>`.
  return createElement(
    as || "div",
    { className: styles.srOnly, ...props },
    children,
  );
}

export { ScreenReaderOnly };
